// Кэш бинарных blob'ов на файловой системе устройства (@capacitor/filesystem).
//
// В WebView Cache API делит квоту с локальной SQLite PowerSync и вычищается
// вместе с данными приложения, поэтому медиа на мобиле держим в Directory.Data.
//
// Доступ к плагину — canonical `window.Capacitor.Plugins.Filesystem`. Через
// `await import("@capacitor/filesystem")` со specifier'ом-переменной bare
// specifier доживал до WebView и не резолвился: `loadFs()` возвращал null, а
// весь медиа-кэш молча превращался в no-op (см. lib/capacitor-plugin.ts).

import { getCapacitorPlugin } from "../../lib/capacitor-plugin";
import type { AudioCacheEntryMeta } from "../../ports/audio-cache.port";

interface FileInfoLike {
	name: string;
	size?: number;
	mtime?: number;
}

type FilesystemApi = {
	readFile: (o: { path: string; directory: string }) => Promise<{ data: string | Blob }>;
	writeFile: (o: {
		path: string;
		data: string;
		directory: string;
		recursive?: boolean;
	}) => Promise<unknown>;
	deleteFile: (o: { path: string; directory: string }) => Promise<void>;
	readdir: (o: { path: string; directory: string }) => Promise<{ files: FileInfoLike[] }>;
	mkdir: (o: { path: string; directory: string; recursive?: boolean }) => Promise<void>;
	stat: (o: { path: string; directory: string }) => Promise<{ size: number; mtime: number }>;
};

const DIRECTORY = "DATA";

async function loadFs(): Promise<FilesystemApi | null> {
	return getCapacitorPlugin<FilesystemApi>("Filesystem");
}

function safeName(key: string): string {
	// Ключ — URL, поэтому кодируем в имя файла без разделителей пути.
	return encodeURIComponent(key).replace(/%/g, "_");
}

/**
 * Обратное преобразование имени файла → ключ.
 *
 * `encodeURIComponent` не экранирует `_`, поэтому ключ, содержащий подчёркивание
 * (например URL картинки `my_file.png`), декодируется неоднозначно. Такие имена
 * отбрасываем по round-trip проверке: `keys()` перечисляет только записи, чей
 * ключ восстанавливается точно. Ключи аудио — `audio-recap:<uuid>` — подчёркиваний
 * не содержат и восстанавливаются всегда.
 */
function keyFromFileName(name: string): string | null {
	try {
		const decoded = decodeURIComponent(name.replace(/_/g, "%"));
		return safeName(decoded) === name ? decoded : null;
	} catch {
		return null;
	}
}

/**
 * Blob → base64 через FileReader.
 *
 * Посимвольная конкатенация `String.fromCharCode` по всем байтам замораживала
 * поток WebView на файлах в единицы мегабайт (PDF, аудио-рекап) и держала в
 * памяти ~3 размера файла. FileReader делает то же нативно и без пиковой
 * склейки строк.
 */
async function blobToBase64(blob: Blob): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
		reader.onloadend = () => {
			const result = reader.result;
			if (typeof result !== "string") {
				reject(new Error("Unexpected FileReader result"));
				return;
			}
			// `data:<mime>;base64,<payload>` → нужен только payload.
			const comma = result.indexOf(",");
			resolve(comma >= 0 ? result.slice(comma + 1) : result);
		};
		reader.readAsDataURL(blob);
	});
}

function base64ToBlob(data: string, type: string): Blob {
	const binary = atob(data);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return new Blob([bytes], { type });
}

export function createFilesystemBlobCache(subdir: string, mimeType: string) {
	const dirPath = `cache/${subdir}`;

	async function listFiles(): Promise<FileInfoLike[]> {
		const fs = await loadFs();
		if (!fs) return [];
		try {
			const res = await fs.readdir({ path: dirPath, directory: DIRECTORY });
			return res.files;
		} catch {
			// Директории ещё нет — кэш пуст.
			return [];
		}
	}

	return {
		async get(key: string): Promise<Blob | null> {
			const fs = await loadFs();
			if (!fs) return null;
			try {
				const res = await fs.readFile({
					path: `${dirPath}/${safeName(key)}`,
					directory: DIRECTORY,
				});
				if (typeof res.data !== "string") return res.data;
				return base64ToBlob(res.data, mimeType);
			} catch {
				return null;
			}
		},

		async put(key: string, blob: Blob): Promise<void> {
			const fs = await loadFs();
			if (!fs) return;
			try {
				await fs.mkdir({ path: dirPath, directory: DIRECTORY, recursive: true }).catch(() => {});
				await fs.writeFile({
					path: `${dirPath}/${safeName(key)}`,
					data: await blobToBase64(blob),
					directory: DIRECTORY,
					recursive: true,
				});
			} catch {
				// Нет места / плагин недоступен — кэш опционален.
			}
		},

		async evict(key: string): Promise<void> {
			const fs = await loadFs();
			if (!fs) return;
			try {
				await fs.deleteFile({ path: `${dirPath}/${safeName(key)}`, directory: DIRECTORY });
			} catch {
				// no-op
			}
		},

		async size(): Promise<number> {
			return (await listFiles()).length;
		},

		async keys(): Promise<string[]> {
			const files = await listFiles();
			const result: string[] = [];
			for (const file of files) {
				const key = keyFromFileName(file.name);
				if (key !== null) result.push(key);
			}
			return result;
		},

		async meta(key: string): Promise<AudioCacheEntryMeta | null> {
			const fs = await loadFs();
			if (!fs) return null;
			try {
				const stat = await fs.stat({
					path: `${dirPath}/${safeName(key)}`,
					directory: DIRECTORY,
				});
				return { bytes: stat.size ?? 0, cachedAt: stat.mtime ?? 0 };
			} catch {
				return null;
			}
		},

		/** readdir на Capacitor 8 отдаёт `size` сразу — обходимся одним вызовом. */
		async sizeBytes(): Promise<number> {
			const files = await listFiles();
			return files.reduce((sum, file) => sum + (file.size ?? 0), 0);
		},
	};
}
